/* cart-rules.js — what a cart may hold of the toilet paper and its trial pack.

   The rules (Arman, 2026-09-25):
     • at most one trial pack, quantity 1
     • never a trial next to toilet paper, one-time or subscribed
   Whichever the shopper added last wins; the other is taken out.

   Theme-side only, so a direct add link can still get past it; a checkout-level block needs
   a Cart & Checkout Validation Function, which the store's plan doesn't allow (docs/TODO.md).

   Two entry points:
     afterAdd(response)  the add paths (product-form.js, cart-upsell.js) pass their add.js
                         response through this before rendering. The added line wins.
     on page load        a cart that broke the rules some other way (a direct add link, another
                         tab, an app) is fixed on the next page it reaches. Liquid flags it
                         (snippets/cart-rules-check.liquid) so a clean cart costs no request.
                         No add to go by, so the newest line wins: the cart is newest-first.

   Handles mirror snippets/cart-rules-check.liquid and snippets/cart-upsell.liquid. */
(function () {
  var TRIAL = 'bamboo-toilet-paper-trial-pack';
  var TP = 'organic-bamboo-toilet-paper';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function isTrial(item) { return item.handle === TRIAL; }
  function isTp(item) { return item.handle === TP; }
  function isFamily(item) { return isTrial(item) || isTp(item); }

  function post(path, body) {
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || ('HTTP ' + r.status));
        return data;
      });
    });
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* What brings `cart` within the rules, or null when it already is: the /cart/update.js
     `updates` map (line key → quantity) and which note explains it. keepKeys are the lines just
     added. The note names what was kept, so it is picked from the winner and what it displaced:
       TpSub / TpOnce               toilet paper (subscribed / one-time) took a trial's place
       TrialOverSub / TrialOverOnce a trial took toilet paper's place
       TrialSwap                    a trial took another trial's place
       TrialQty                     the same trial was added again (quantity back to 1) */
  function plan(cart, keepKeys) {
    var items = cart.items || [];
    var trials = items.filter(isTrial);
    if (!trials.length) return null;

    var winner = null;
    for (var i = 0; i < items.length && !winner; i++) {
      if (isFamily(items[i]) && keepKeys.indexOf(items[i].key) !== -1) winner = items[i];
    }
    for (var j = 0; j < items.length && !winner; j++) {
      if (isFamily(items[j])) winner = items[j];
    }

    var updates = {};
    var changed = false;
    var removedTpSub = false;
    var removedTpOnce = false;
    var removedTrial = false;
    items.forEach(function (item) {
      if (isTp(winner) && isTrial(item)) { updates[item.key] = 0; changed = true; }
      if (isTrial(winner)) {
        if (isTp(item)) {
          updates[item.key] = 0; changed = true;
          if (item.selling_plan_allocation) removedTpSub = true; else removedTpOnce = true;
        } else if (isTrial(item) && item !== winner) {
          updates[item.key] = 0; changed = true; removedTrial = true;
        } else if (item === winner && item.quantity !== 1) {
          updates[item.key] = 1; changed = true;
        }
      }
    });
    if (!changed) return null;

    var note;
    if (isTp(winner)) note = winner.selling_plan_allocation ? 'TpSub' : 'TpOnce';
    else if (removedTpSub) note = 'TrialOverSub';
    else if (removedTpOnce) note = 'TrialOverOnce';
    else note = removedTrial ? 'TrialSwap' : 'TrialQty';
    return { updates: updates, note: note };
  }

  function sectionIds() {
    var drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.sectionIdsToRender === 'function' ? drawer.sectionIdsToRender() : [];
  }

  /* The note says what was kept and why the other line went. Texts are Theme Settings → Cart,
     one per case, carried on <cart-drawer> as data-rules-note-* (outside #CartDrawer, so
     re-renders don't drop them); a blank one means no note. It goes at the top of the line list
     and is wiped by the next re-render, which is when it stops being news. */
  function showNote(key) {
    var drawer = document.querySelector('cart-drawer');
    var text = drawer && key && drawer.dataset['rulesNote' + key];
    var list = document.getElementById('CartDrawer-CartItems');
    if (!text || !list || list.querySelector('.cart-rules-note')) return;
    var note = document.createElement('p');
    note.className = 'cart-rules-note';
    note.setAttribute('role', 'status');
    note.textContent = text;
    list.insertBefore(note, list.firstChild);
  }

  /* Resolves to the state the caller should render: the add response itself when the cart is
     within the rules, else the same response carrying the sections of the corrected cart, with
     `cartRulesNote` set to the key the caller passes to showNote() once it has rendered. Any
     failure here resolves to the untouched add response: the add already succeeded, and the page-load
     check catches the cart next time. */
  function afterAdd(response) {
    var added = response.items || [response];
    var keepKeys = added.map(function (item) { return item.key; }).filter(Boolean);
    return getCart()
      .then(function (cart) {
        var fix = plan(cart, keepKeys);
        if (!fix) return response;
        return post('cart/update.js', {
          updates: fix.updates,
          sections: sectionIds(),
          sections_url: window.location.pathname
        }).then(function (state) {
          var merged = {};
          for (var k in response) merged[k] = response[k];
          merged.sections = state.sections;
          merged.cartRulesNote = fix.note;
          return merged;
        });
      })
      .catch(function (error) {
        console.error('[cart-rules] could not apply:', error);
        return response;
      });
  }

  /* Page-load repair. The cart page has no drawer to repaint, so it reloads. Elsewhere the drawer
     sections are swapped in place without opening it; the note waits in there for the shopper. */
  function repairOnLoad() {
    var drawer = document.querySelector('cart-drawer');
    var ids = sectionIds();
    getCart()
      .then(function (cart) {
        var fix = plan(cart, []);
        if (!fix) return;
        if (!drawer || !ids.length) {
          return post('cart/update.js', { updates: fix.updates }).then(function () { window.location.reload(); });
        }
        return post('cart/update.js', { updates: fix.updates, sections: ids, sections_url: window.location.pathname })
          .then(function (state) {
            drawer.getSectionsToRender().forEach(function (section) {
              var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
              if (el && state.sections[section.id]) el.innerHTML = drawer.getSectionInnerHTML(state.sections[section.id], section.selector);
            });
            var overlay = document.getElementById('CartDrawer-Overlay');
            if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
            showNote(fix.note);
          });
      })
      .catch(function (error) { console.error('[cart-rules] page-load repair failed:', error); });
  }

  window.WythoutCartRules = { afterAdd: afterAdd, showNote: showNote };

  /* Deferred, so the DOM is parsed but cart-drawer.js (also deferred, later in the document) may
     not have defined <cart-drawer> yet; its section methods are needed for the repaint. */
  if (document.querySelector('[data-cart-rules-fix]')) {
    if (document.querySelector('cart-drawer') && window.customElements) {
      customElements.whenDefined('cart-drawer').then(repairOnLoad);
    } else {
      repairOnLoad();
    }
  }
})();
